import React, { useState, useEffect } from "react";
import axios from "axios";

const STATIC_TOKEN =
  "N2o0ZjZlZVFBRUxVL1laanlBczQ0UT09OjrGQtx134tUoB8T7vu8HoWyMTN3NjlhZWQrNDMyZGpydGVRR3VIQT09OjqOhX0XNADMMEojPyPpiZlA";

interface Destination {
  destinationId: string;
  destinationTitle: string;
}

const FamilyTravelCTALanding: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    travelDate: "",
    destination: "",
  });

  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.post(
          "https://travelnrelax.com/tadmin/api/pages/destinations",
          {},
          {
            headers: {
              token: STATIC_TOKEN,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.data.success) {
          setDestinations(response.data.data.destinations);
        }
      } catch (error) {
        console.error("Error fetching destinations:", error);
      }
    };

    fetchDestinations();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    const selectedDestination = destinations.find(
      (d) => d.destinationTitle === formData.destination
    );

    const payload = {
      fullname: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      travel_date: formData.travelDate.split("-").reverse().join("-"),
      destination: selectedDestination?.destinationId || "",
    };

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        "https://travelnrelax.com/tadmin/api/enquiries/lead_contact",
        payload,
        {
          headers: {
            token: STATIC_TOKEN,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("API raw response:", response);

      let parsedData;
      try {
        const jsonStartIndex = response.data.indexOf("{");
        parsedData = JSON.parse(response.data.slice(jsonStartIndex));
      } catch (parseError) {
        console.error("Failed to parse response JSON:", parseError);
        setErrorMessage("Unexpected server response. Please try again later.");
        return;
      }

      if (parsedData.status === "200") {
        setSuccessMessage("Thanks! Our family travel expert will contact you soon.");
        setFormData({
          fullName: "",
          email: "",
          phone: "",
          travelDate: "",
          destination: "",
        });
      } else {
        setErrorMessage(parsedData.message || "Something went wrong. Please try again later.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage("An error occurred while submitting the form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-16 mt-5 bg-gradient-to-b from-blue-50 via-white to-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="bg-white rounded-2xl shadow p-8 sm:p-12 md:p-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl sm:text-4xl font-akshar font-bold text-blue-900 leading-tight mb-6">
              Let’s plan the kind of vacation they’ll remember forever
            </h2>
            <p className="text-gray-700 text-lg mb-6 font-poppins">
              Whether you're dreaming of sailing with Mickey or strolling
              through the streets of Florence, we’ll take care of every
              detail—so you can focus on making memories that matter.
            </p>
            <ul className="space-y-2 font-medium text-gray-600 text-base font-alumnisans">
              <li>✅ Expert itinerary planning</li>
              <li>✅ Park passes, flights & cruise bookings</li>
              <li>✅ Handpicked family-friendly experiences</li>
            </ul>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-xl p-6 shadow-md space-y-6"
          >
            {successMessage && (
              <div className="p-4 text-green-800 bg-green-100 border border-green-300 rounded">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="p-4 text-red-800 bg-red-100 border border-red-300 rounded">
                {errorMessage}
              </div>
            )}

            <div>
              <label
                htmlFor="fullName"
                className="font-poppins block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="font-poppins block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="font-poppins block text-sm font-medium text-gray-700 mb-1"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="destination"
                className="block font-anekbangla font-semibold text-gray-700"
              >
                Destination List
              </label>
              <select
                id="destination"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">---Select---</option>
                {destinations.map((dest) => (
                  <option
                    key={dest.destinationId}
                    value={dest.destinationTitle}
                  >
                    {dest.destinationTitle}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="travelDate"
                className="font-poppins block text-sm font-medium text-gray-700 mb-1"
              >
                Preferred Travel Date
              </label>
              <input
                type="date"
                id="travelDate"
                name="travelDate"
                value={formData.travelDate}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-md px-4 py-2 text-gray-800 focus:ring-2 focus:ring-blue-400 outline-none"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-blue-900 hover:bg-blue-700 text-white text-lg font-medium py-3 px-6 rounded-lg transition ${
                  isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Submitting..." : "Talk to a Travel Expert"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FamilyTravelCTALanding;
